import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import type { ReactNode } from 'react'

import { Layout, Seo } from '@/components/generals'
import { AboutSlider } from '@/components/landing/about-slider'
import { TeamMember } from '@/components/landing/team-member'
import { getTeamMembers } from '@/lib/cms'

interface PillarLink {
    title: string
    description: string
    meta: string
    href: string
}

const PILLARS: PillarLink[] = [
    {
        title: 'Personalized Consulting',
        description:
            'Plans tailored to your vision, with actionable insights from a practitioner who understands what it takes to build a thriving practice.',
        meta: '· Annual coaching · Power sessions',
        href: '/what-we-do'
    },
    {
        title: 'Transformational One-on-One Coaching',
        description:
            'A customized roadmap and a seasoned expert who shoots straight with you, aligning your professional journey with your personal goals.',
        meta: '· One-on-one with Dr. Brad Chvatal',
        href: '/what-we-do#annual-coaching'
    },
    {
        title: 'Proven Track Record of Success',
        description:
            "Built on one orthodontist's journey to three thriving locations, and on the strategic planning and team building that made it possible.",
        meta: '· Read the client stories',
        href: '/showcases'
    }
]

const WhoWeArePage = ({ team = [] }: InferGetStaticPropsType<typeof getStaticProps>): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Meet Dr. Brad Chvatal and the Team'
                description='Meet Dr. Brad Chvatal, orthodontist and founder of Czechmate Consulting, and the trainers who coach case acceptance, collections, clinical flow and scheduling.'
            />

            <>
                <div className='hero-section-secondary overflow-hidden' id='hero'>
                    <div className='container'>
                        <div className='hero-content'>
                            <div className='mdl-layout-grid who-we-are-hero-grid'>
                                <div className='content' id='mdl-node-_5cb38d00-8bed-4132-55fd-31f857d91ca6-c8bc8f09'>
                                    <h1 className='hero-text-content no-margins'>
                                        <div className='hero-text-word'>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _1'>Your</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _2'>Czech</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _3'>Mate</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _4'>in</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _5'>the</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _6'>Game</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _7'>of</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _8'>Orthodontics</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _9'>(and</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _10'>Life)</div>
                                            </div>
                                        </div>
                                    </h1>
                                    <div className='margin-top-1em'>
                                        <div className='animation-on-page-load-1'>
                                            <div className='hero-description'>
                                                Brad Chvatal isn&apos;t your typical orthodontic consultant, and that&apos;s
                                                exactly the point. He and his team bring straight talk, real-world results
                                                and a refreshing approach to helping orthodontists build practices, and
                                                lives, they actually love.
                                            </div>
                                        </div>
                                        <div className='animation-on-page-load-2'>
                                            <div className='hero-buttons-holder'>
                                                <a className='button mdl-button' href='/contact'>let&apos;s talk</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='content relative'>
                                    <div className='who-we-are-images-container animation-on-page-load-3'>
                                        <span className='who-we-are-hero-pattern' aria-hidden='true' />
                                        <img
                                            className='who-we-are-image'
                                            src='/images/czechmate/brad-headshot.jpg'
                                            alt='Dr. Brad Chvatal, founder of Czechmate Consulting'
                                        />
                                        <img
                                            className='who-we-are-image-2'
                                            src='/images/czechmate/team-photo-wide.jpg'
                                            alt='The Czechmate Consulting team standing together in branded t-shirts'
                                        />
                                        <img
                                            className='who-we-are-image-3'
                                            src='/images/czechmate/team-banner.jpg'
                                            alt='Brad and two Czechmate trainers smiling in front of tall office windows'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Client logo row hidden: the real site shows no client logos. */}
                    </div>
                </div>

                <div className='section' id='our-story'>
                    <div className='container'>
                        <AboutSlider />
                    </div>
                    <div className='section-background' />
                </div>

                <div className='section overflow-hidden' id='team'>
                    <div className='container no-padding-bottom'>
                        <div className='mdl-layout-grid grid-stats fade-in-on-scroll'>
                            <div className='content' id='mdl-node-_8b9690af-ea2e-074d-5062-4c0f5ff048f9-c8bc8f09'>
                                <div className='stats-content'>
                                    <div className='big-number'>3</div>
                                    <div>locations Brad grew from his own practice</div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-c12ff8f4-cd3e-c756-ccf3-c59feede3440-c8bc8f09'>
                                <div className='stats-content'>
                                    <div className='big-number'>2x</div>
                                    <div>production in one year, from a shift in mindset</div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-_94bc1836-e9ab-d127-a4fd-8026a0e8ad22-c8bc8f09'>
                                <div className='stats-content'>
                                    <div className='big-number'>&lt;1%</div>
                                    <div>past due for some of our clients</div>
                                </div>
                            </div>
                        </div>
                        <div className='mdl-layout-grid grid-2-col'>
                            <div className='content' id='mdl-node-_12e042fe-1ac4-3feb-e96d-934376c17ee4-c8bc8f09'>
                                <div className='fade-in-move-on-scroll'>
                                    <h2 className='no-margins'>Meet Your Coaching Team</h2>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-_12e042fe-1ac4-3feb-e96d-934376c17ee8-c8bc8f09'>
                                <div className='fade-in-move-on-scroll'>
                                    <div>
                                        Sam brings 18 years of orthodontic experience, Heather 16, Marissa 11 and Itzy
                                        6. They are the same trainers who helped Brad build and scale his own practice.
                                    </div>
                                </div>
                                <div className='fade-in-move-on-scroll'>
                                    <div className='margin-top-2em'>
                                        <div className='flex-horizontal'>
                                            <a className='button button-link mdl-inline-block' href='/contact'>
                                                <div>Book a free consultation</div>
                                                <span className='button-icon' aria-hidden='true' />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='mdl-layout-grid team-members-grid fade-in-on-scroll'>
                            {team.map((member) => (
                                <div className='content' key={member.id}>
                                    <TeamMember member={member} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className='section overflow-hidden' id='your-practice-elevated'>
                    <div className='container'>
                        <div className='mdl-layout-grid grid-2-col'>
                            <div className='content' id='mdl-node-cb6323aa-a2ba-4ad1-91d9-971a9286ceb8-c8bc8f09'>
                                <div className='fade-in-move-on-scroll'>
                                    <h2 className='no-margins'>Your Practice Elevated</h2>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-cb6323aa-a2ba-4ad1-91d9-971a9286cebc-c8bc8f09'>
                                <div className='fade-in-move-on-scroll'>
                                    <div>
                                        Czechmate Consulting pairs real-world expertise with personalized strategy to
                                        elevate orthodontic practices across the U.S. Because we&apos;re led by a
                                        seasoned orthodontist, every plan reflects your vision, and growth never has to
                                        cost you your life balance.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='image-holder-with-animation'>
                            <div className='image-with-animation-container'>
                                <img
                                    className='image-with-animation'
                                    src='/images/czechmate/team-photo.jpg'
                                    alt='The Czechmate Consulting team standing together in branded t-shirts'
                                />
                            </div>
                        </div>
                        <div className='open-positions-list fade-in-move-on-scroll'>
                            {PILLARS.map((pillar) => (
                                <a className='open-position-link mdl-inline-block' href={pillar.href} key={pillar.title}>
                                    <div className='open-position-text'>
                                        <div className='heading-small heading-font-color'>{pillar.title}</div>
                                        <div>
                                            {pillar.description}
                                            <br />
                                            <br />
                                            {pillar.meta}
                                        </div>
                                    </div>
                                    <span className='open-position-icon' aria-hidden='true' />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </>
        </Layout>
    )
}

export const getStaticProps = (async () => {
    const team = await getTeamMembers()
    return { props: { team }, revalidate: 60 }
}) satisfies GetStaticProps

export default WhoWeArePage
